/**
 * Helpers pour lire une erreur throw par `libs/shared/src/core/services/network/Api.ts`
 * et en produire un message métier utilisable côté UI (toast, bannière, table).
 *
 * Shape de l'erreur (Api.ts) :
 * ```
 * {
 *   type: 'server',
 *   nativeError,
 *   statusCode: response.status,
 *   config: { baseURL, url, params },
 *   ...response.data          // = { status: 'fail' | 'error', data: { errorMessage }, message }
 * }
 * ```
 *
 * Codes `errorMessage` observés sur l'Extranet API v1.8.4 : `INVALID_TOKEN` (401),
 * `INVALID_USER_TYPE` (403 fonctionnel), `INVALID_CREDENTIALS` (403 au login),
 * `SCOPE_NOT_ON_ACCOUNT` (403, valeur de périmètre inconnue du compte, `message`
 * localisé), `PASSENGER_ACCOUNT_NOT_FOUND` (404). Sur un 400 de validation, `errorMessage`
 * contient un dump JSON class-validator : ne jamais l'afficher.
 */

export interface ApiErrorShape {
  statusCode?: number;
  status?: number;
  response?: { status?: number };
  data?: { errorMessage?: string };
  errorMessage?: string;
  message?: string;
}

export const getHttpStatus = (err: unknown): number | undefined => {
  if (err && typeof err === 'object') {
    const e = err as ApiErrorShape;
    return e.statusCode ?? e.response?.status ?? e.status;
  }
  return undefined;
};

export const getBackErrorMessage = (err: unknown): string | undefined => {
  if (err && typeof err === 'object') {
    const e = err as ApiErrorShape;
    return e.data?.errorMessage ?? e.errorMessage;
  }
  return undefined;
};

export const hasBackError = (err: unknown, code: string): boolean => getBackErrorMessage(err) === code;

export const getBackMessage = (err: unknown): string | undefined => {
  if (err && typeof err === 'object') {
    const e = err as ApiErrorShape;
    return e.message;
  }
  return undefined;
};

/**
 * Produit un titre + description user-friendly pour un toast/bannière d'erreur.
 * Priorise les cas fonctionnels connus avant de retomber sur un message générique.
 * Le `message` du back est localisé via `Accept-Language` : on l'affiche sur les
 * refus fonctionnels (403 hors permission, 422), jamais sur un 400 de validation
 * ni sur un 500.
 */
export interface UserErrorMessage {
  title: string;
  description: string;
}

export const toUserErrorMessage = (err: unknown, fallbackTitle: string): UserErrorMessage => {
  if (!err) {
    return { title: fallbackTitle, description: 'Une erreur inconnue est survenue. Réessayez.' };
  }
  const status = getHttpStatus(err);
  const errorMessage = getBackErrorMessage(err);

  if (status === 403 && errorMessage === 'INVALID_CREDENTIALS') {
    return {
      title: 'Identifiants incorrects',
      description: 'Email ou mot de passe incorrect.',
    };
  }
  if (status === 403 && errorMessage === 'INVALID_USER_TYPE') {
    return {
      title: 'Permission manquante',
      description: "Vous n'avez pas les droits pour effectuer cette action. Contactez votre administrateur Allocab.",
    };
  }
  if (status === 403) {
    // Refus fonctionnel (ex. `SCOPE_NOT_ON_ACCOUNT`) : le back envoie un message
    // localisé (`Accept-Language`) qui explique la règle — on le montre.
    return {
      title: 'Action refusée',
      description:
        getBackMessage(err) || 'Cette opération est refusée pour votre compte. Contactez votre administrateur Allocab.',
    };
  }
  if (status === 401) {
    return {
      title: 'Session expirée',
      description: 'Votre session a expiré. Reconnectez-vous pour continuer.',
    };
  }
  if (status === 404) {
    return {
      title: 'Ressource introuvable',
      description: "La ressource demandée n'existe pas ou plus.",
    };
  }
  if (status === 422) {
    return {
      title: 'Action impossible',
      description: getBackMessage(err) || "Cette opération n'est pas autorisée dans l'état actuel.",
    };
  }
  if (status === 400) {
    return {
      title: 'Données invalides',
      description: 'Vérifiez les champs saisis et réessayez.',
    };
  }
  if (typeof status === 'number' && status >= 500) {
    return {
      title: 'Service indisponible',
      description: 'Le service Allocab est momentanément indisponible. Réessayez dans quelques instants.',
    };
  }
  if (!status) {
    return {
      title: 'Connexion perdue',
      description: 'Impossible de joindre le serveur. Vérifiez votre connexion et réessayez.',
    };
  }
  return {
    title: fallbackTitle,
    description: 'Réessayez ou contactez votre administrateur Allocab.',
  };
};
